/**
 * The filter bar's DEFINITIONS: what can be filtered, and what each field is.
 *
 * Handed to the bar by its host rather than read off one model, because the
 * two hosts disagree about where fields come from. A report has one model and
 * offers every field in it. A dashboard's tiles sit on several, so its fields
 * are the union of theirs — and a field there carries a note saying how many
 * tiles it reaches, which no single model could supply.
 */
import { canFilter, filterValueType, type ReportModel } from '@clockwise/semantic-core';
import type { FilterTypeOf } from './filter-actions';

export interface FilterField {
  /** The dimension or measure id — what a rule's `field` names. */
  id: string;
  label: string;
  description?: string;
  /**
   * One sentence telling this field apart from another reading the same.
   *
   * Only where it is needed: `srController` and `accountSrController` are both
   * "Sr. Controller" and mean different people.
   */
  hint?: string;
  /**
   * The model that answers for this field — its operators, its value type,
   * and where its members are fetched from.
   *
   * Two models naming the same dimension id mean the same thing (both compile
   * `customer` to `customer_id`), so any one that has it will do.
   */
  model: ReportModel;
  /** A muted aside, e.g. "applies to 2 of 3 tiles". Shown on the chip and in the menu. */
  note?: string;
}

/**
 * Every field one model can filter on: its filterable dimensions, then the
 * measures with a per-row value.
 *
 * Measures LAST: most filters are about WHICH rows, and a reader looking for
 * "Customer" should not have to read past nine hour columns to find it.
 */
export function filterFieldsOf(model: ReportModel): FilterField[] {
  const dimensions = model.dimensions.filter(canFilter).map((d) => ({
    id: d.id,
    label: d.label,
    description: d.description,
    model,
  }));
  // A ratio has no row-level value at all and is therefore not offered;
  // `filterValueType` is the one reader of that rule, shared with the guard
  // that would refuse it.
  const measures = model.measures
    .filter((m) => filterValueType(model, m.id) !== null)
    .map((m) => ({ id: m.id, label: m.label, description: m.description, model }));
  return [...dimensions, ...measures];
}

/** {@link FilterTypeOf} over a set of definitions, each answered by its own model. */
export function filterTypeOf(fields: readonly FilterField[]): FilterTypeOf {
  const byId = new Map(fields.map((f) => [f.id, f]));
  return (id) => {
    const field = byId.get(id);
    return field ? filterValueType(field.model, id) : null;
  };
}
